import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Col, Row } from 'antd';
import { withAsyncActions } from 'react-async-client';
import { path, pathOr, prop, contains } from 'ramda';
import cx from 'classnames';
import Translate from 'react-translate-component';

import { WORKFLOW_EVENT_TYPES } from 'constants/events';
import EventResultForm from 'components/modals/events/result/EventResultForm';
import ErrorHTML from 'components/common/form/ErrorHTML';
import { DefinitionHeader } from 'components/common/fields/DefinitionHeader';
import { getEvent, getUser, updateEvent } from 'actions/asyncActions';
import ModalCloseButton from 'components/modals/ModalCloseButton';
import { fullAutoOptions } from 'constants/asyncClient';
import EventEditForm from './card/EventEditForm';
import EventView from './card/EventView';
import EventResult from './card/EventResult';
import { EventEditButton, EditResultButton } from './result/EventButtons';
import EventWorkflowForm from './result/EventWorkflowForm';
import { contactIsRequired, companyIsRequired } from 'helpers/eventHelper';
import { MASSWORKSHEET_TYPES_ID } from 'constants/worksheet';
import CollapsibleModalContainer from '../CollapsibleModalContainer';

const stateToProps = (state) => ({
    user: getUser.selectData(state),
});

const isRequiredFieldsFilled = props => {
    const eventConfig = path(['getEvent', 'data', 'view'], props) || {};
    const companyPermitted = eventConfig.company || eventConfig.companyAndEmployee;
    const contactPermitted = eventConfig.employee || eventConfig.companyAndEmployee || eventConfig['co-executive'];

    const company = path(['getEvent', 'data', 'details', 'company'], props);
    const contact = path(['getEvent', 'data', 'details', 'contact'], props);

    const contactRequired = contactPermitted && contactIsRequired(props, path(['getEvent', 'data', 'type']));
    const companyRequired = companyPermitted && companyIsRequired(props, path(['getEvent', 'data', 'type']));

    return (companyRequired ? company : true) && (contactRequired ? contact : true);
};

@connect(stateToProps)
@withAsyncActions(props => ({
    getEvent: getEvent
        .withParams({ id: props.params.id })
        .withPayload(({ params }) => params.id)
        .withOptions(fullAutoOptions),
    updateEvent: updateEvent
        .withParams({ id: props.params.id })
        .withSuccessHandler(({ getEvent: { refresh } }) => refresh())
        .withOptions({ resetOnUnmount: true }),
}))
export default class EventModal extends Component {
    static propTypes = {
        getEvent: PropTypes.object.isRequired,
        updateEvent: PropTypes.object.isRequired,
        user: PropTypes.object.isRequired,
        close: PropTypes.func.isRequired,
        params: PropTypes.shape({
            id: PropTypes.number.isRequired,
            text: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.object,
            ]),
            type: PropTypes.string,
            initialEnterResultState: PropTypes.bool,
            value: PropTypes.string,
        })
    };

    constructor(props) {
        super(props);

        this.state = {
            isEditEvent: false,
            isEnterResult: props.params.initialEnterResultState
        };
    }

    toggleEdit = () => {
        this.setState((prev) => ({ isEditEvent: !prev.isEditEvent }));
    }

    toggleResult = () => {
        this.setState((prev) => ({ isEnterResult: !prev.isEnterResult }));
    }

    renderTitle() {
        const { data, meta } = this.props.getEvent;
        const title = this.props.params.value;
        const name = pathOr(title, ['name'], data.details);
        const projectInfo = path(['project', 0, 'text'], data.details);

        return meta.success && (
            data.type !== 0 ? (
                <div className='event-modal-header'>
                    <span>{ name }</span>
                    <small>{ projectInfo }</small>
                </div>
            ) : (
                <div className='event-modal-header'>
                    <span><Translate content='error.title' /></span>
                </div>
            )
        );
    }

    renderFooter() {
        const { close, updateEvent: { meta } } = this.props;

        return (
            <Row>
                <Col xs={{ span: 18 }} className="alert-inline">
                    <ErrorHTML meta={meta} />
                </Col>
                <Col xs={{ span: 6 }}>
                    <ModalCloseButton close={close} />
                </Col>
            </Row>
        );
    }

    render() {
        const { getEvent, getEvent: { data, meta, data: { edit, projectType } }, close, user: { MAPLicense } } = this.props;
        const type = path(['data', 'details', 'xmltype'], getEvent);
        const eventType = data.type !== 0;

        const isWorkflowType = contains(data.type, WORKFLOW_EVENT_TYPES);
        const { isEditEvent, isEnterResult } = this.state;

        const EventRenderComponent = isEditEvent ? EventEditForm : EventView;
        const title = this.renderTitle();
        const footer = eventType && this.renderFooter();
        const noDocumentExist = !path(['documents', '0', 'id'], data) || !data.shortResult;
        const isMassEditable = contains(projectType, MASSWORKSHEET_TYPES_ID) ? MAPLicense : true;

        const isEditable = edit ? (isMassEditable && edit.editable) : noDocumentExist;
        const isClosable = edit ? edit.closeable && noDocumentExist : isEditable;

        const projectTypeId = prop('projectType', data);

        const isRequiredFieldsNotFilled = !isRequiredFieldsFilled(this.props);
        const isResultNotAllowed = isEditEvent || !isMassEditable || isRequiredFieldsNotFilled;
        const containerClassName = cx('event-modal modal-normal', { error: !eventType });

        return (
            <CollapsibleModalContainer meta={meta} title={title} footer={footer} className={containerClassName}>
                { eventType && <DefinitionHeader translate='events.title' iconType='calendar' />}
                { meta.success && (
                    eventType ? (
                        <div>
                            <EventRenderComponent close={this.toggleEdit} item={data} />

                            <EventEditButton
                                toggleEdit={this.toggleEdit}
                                isEditEvent={isEditEvent}
                                isEditable={isEditable}
                                isEnterResult={isEnterResult} />

                            <EventResult getEvent={getEvent} />

                            { isClosable && (
                                <div>
                                    <DefinitionHeader translate='events.enterResult' iconType='building' />
                                    { !isEnterResult ? (
                                        <EditResultButton
                                            isResultNotAllowed={isResultNotAllowed}
                                            isEditEvent={isEditEvent}
                                            toggleResult={this.toggleResult}
                                        />
                                    ) : isWorkflowType ? (
                                            <EventWorkflowForm
                                                close={this.toggleResult}
                                                closeModal={close}
                                                event={data}
                                            />
                                        ) : (
                                            <EventResultForm
                                                projectTypeId={projectTypeId}
                                                item={data}
                                                close={this.toggleResult}
                                                type={type}
                                            />
                                        )
                                    }
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className='non-existent-event'>
                            <Translate content='errors.notExistEvent' />
                        </div>
                    )
                )}
            </CollapsibleModalContainer>
        );
    }
}
